/**
 * Conversão de variáveis entre o formato amigável da UI e o formato numérico
 * que o backend/Meta entende.
 *
 * O backend armazena o `bodyText` com placeholders posicionais:
 *   {{1}} = nome do cliente
 *   {{2}} = nome do serviço
 *   {{3}} = horário do agendamento
 *
 * Na UI mostramos aliases legíveis em pt-BR (com acento):
 *   {{nome}}    ↔ {{1}}
 *   {{serviço}} ↔ {{2}}
 *   {{horário}} ↔ {{3}}
 *
 * Estas funções são puras e usadas em ambas as direções:
 * - `toBackend` ANTES de enviar (createTemplate / submitTemplate).
 * - `toDisplay` ao popular o textarea de edição e ao exibir o texto cru.
 *
 * O `TemplateBodyPreview` resolve TANTO aliases quanto numéricos, então o
 * preview funciona no modal (texto alias) e no card (texto numérico salvo).
 */

/**
 * Aliases canônicos (com acento) mapeados para o índice posicional do backend.
 * A ordem aqui também define a ordem de exibição das instruções de variáveis.
 * @type {ReadonlyArray<{ alias: string, index: string, example: string, desc: string }>}
 */
export const TEMPLATE_VARIABLES = Object.freeze([
  {
    alias: "nome",
    index: "1",
    example: "João Silva",
    desc: "será substituído pelo nome do cliente",
  },
  {
    alias: "serviço",
    index: "2",
    example: "Corte de Cabelo",
    desc: "será substituído pelo serviço agendado",
  },
  {
    alias: "horário",
    index: "3",
    example: "14:00",
    desc: "será substituído pelo horário do agendamento",
  },
]);

/**
 * Escapa caracteres especiais de regex em uma string literal.
 * @param {string} value
 * @returns {string}
 */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Remove marcas de acentuação (combining diacritical marks U+0300–U+036F),
 * para tratar "serviço"→"servico" e "horário"→"horario" como equivalentes.
 * @param {string} value
 * @returns {string}
 */
function stripDiacritics(value) {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * Converte aliases amigáveis ({{nome}}, {{serviço}}, {{horário}}) para o
 * formato posicional do backend ({{1}}, {{2}}, {{3}}).
 *
 * Regex tolerante a espaços internos ({{  nome  }}) e case-insensitive. Para
 * robustez contra usuários que digitem sem acento ("servico", "horario"), as
 * variantes sem acento também são aceitas.
 *
 * @param {string} text
 * @returns {string}
 */
export function toBackend(text) {
  if (typeof text !== "string") return "";
  let result = text;
  TEMPLATE_VARIABLES.forEach(({ alias, index }) => {
    // Aceita o alias canônico e, quando há acento, a variante sem acento.
    const aliases = [alias];
    const stripped = stripDiacritics(alias);
    if (stripped !== alias) aliases.push(stripped);

    aliases.forEach((a) => {
      const pattern = new RegExp(
        `\\{\\{\\s*${escapeRegExp(a)}\\s*\\}\\}`,
        "gi",
      );
      result = result.replace(pattern, `{{${index}}}`);
    });
  });
  return result;
}

/**
 * Converte o formato posicional do backend ({{1}}, {{2}}, {{3}}) de volta para
 * aliases amigáveis ({{nome}}, {{serviço}}, {{horário}}).
 *
 * @param {string} text
 * @returns {string}
 */
export function toDisplay(text) {
  if (typeof text !== "string") return "";
  let result = text;
  TEMPLATE_VARIABLES.forEach(({ alias, index }) => {
    const pattern = new RegExp(`\\{\\{\\s*${index}\\s*\\}\\}`, "g");
    result = result.replace(pattern, `{{${alias}}}`);
  });
  return result;
}

/**
 * Resolve TODOS os placeholders (aliases e numéricos) por valores de exemplo,
 * para renderizar o preview da bolha de WhatsApp.
 *
 * @param {string} text
 * @returns {string}
 */
export function resolveExamples(text) {
  if (typeof text !== "string") return "";
  let result = text;
  TEMPLATE_VARIABLES.forEach(({ alias, index, example }) => {
    const aliasPattern = new RegExp(
      `\\{\\{\\s*${escapeRegExp(alias)}\\s*\\}\\}`,
      "gi",
    );
    const strippedAlias = stripDiacritics(alias);
    const numericPattern = new RegExp(`\\{\\{\\s*${index}\\s*\\}\\}`, "g");
    result = result.replace(aliasPattern, example).replace(numericPattern, example);
    if (strippedAlias !== alias) {
      const strippedPattern = new RegExp(
        `\\{\\{\\s*${escapeRegExp(strippedAlias)}\\s*\\}\\}`,
        "gi",
      );
      result = result.replace(strippedPattern, example);
    }
  });
  return result;
}
